/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Divider from '@material-ui/core/Divider';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import Link from '@material-ui/core/Link';
import Tooltip from '../../../Tooltip';
import { CardContext } from '../../CardContext';
import { dataTestId } from '../../../../../shared/utils/versionUtils';
import { checkTitleAndRemoveWhitespaces, toEllipsis } from '../../../../../shared/utils/stringUtils';
import { selectVersion } from '../../../../../redux/features/configuration/configuration.slice';
import Entry from '../Entry';
import SimpleModal from '../../../simpleModal/SimpleModal';
import { getAttachmentLabel } from '../../../../draft/attachments/services/attachments.helper';
import { callApi } from '../../../../../api/helpers/api.helpers';
import draftApi from '../../../../../api/draft.api';

/**
 * Custom component that handles attachment previews
 * @param entry
 * @param readOnly
 * @param customClassName
 * @returns {JSX.Element}
 *  - if entry.value can be parsed then return the attachment preview component
 *  - if entry.value is not parseable then return an Entry component
 * @constructor
 */
export default function AttachmentsPreviewEntry({ entry, readOnly, customClassName }) {
  const { t } = useTranslation();
  const { classes } = useContext(CardContext);
  const selectedVersion = useSelector(selectVersion);
  const [previewFile, setPreviewFile] = useState(undefined);

  // parse the attachment object if possible
  const attachment = {};
  try {
    Object.assign(attachment, JSON.parse(entry?.value));
  } catch (error) {
    // return entry if no attachment was found
    return (
      <Entry
        title={entry?.title}
        value={entry?.value}
        type={entry?.type}
        onClick={entry?.onClick}
        noScroll={entry?.noScroll || !entry?.value}
        readOnly={readOnly}
        customClassName={customClassName}
        hideLinkTo={entry?.hideLinkTo}
      />
    );
  }

  const { title, hasDivider } = entry;
  const getXsSizes = () => {
    if (title && attachment) {
      return [5, 7];
    }
    if (title) {
      return [12, false];
    }
    if (attachment) {
      return [false, 12];
    }
    return [false, false];
  };
  const [xsTitle, xsValue] = getXsSizes();
  const entryTitle = checkTitleAndRemoveWhitespaces(title);

  const handleItemPreview = async () => {
    const attachmentBlob = await callApi(draftApi.fetchAttachment)(
      attachment.submissionId,
      attachment.id,
      attachment.originalFileName,
      attachment.mimeType,
    );
    const attachmentObjectUrl = URL.createObjectURL(attachmentBlob);
    setPreviewFile(attachmentObjectUrl);
  };

  return (
    <>
      <Grid item container spacing={2} className={classes.content}>
        {title && (
          <Grid item xs={xsTitle}>
            <Typography
              variant="body2"
              display="inline"
              {...dataTestId({
                value: `attachmentPreview_summary_${entryTitle}-title--${selectedVersion.value}`,
              })}
            >
              <Trans i18nKey="title" title={title}>
                {title}
              </Trans>
            </Typography>
          </Grid>
        )}
        {attachment && (
          <Grid item className={classNames(classes.entryValueGridItem)} xs={xsValue} style={{ cursor: 'pointer' }}>
            <Tooltip
              title={attachment?.originalFileName}
              placement="top"
              arrow
              disableHoverListener={attachment?.originalFileName?.length < 20}
            >
              <Typography
                variant="body2"
                noWrap
                display="inline"
                {...dataTestId({
                  value: `attachmentsPreview_summary_${entryTitle}-value--${selectedVersion.value}`,
                })}
              >
                <Link
                  id={`entry-card-link-${attachment.id}`}
                  form={`dummy-form-${uuidv4()}`}
                  variant="body2"
                  onClick={handleItemPreview}
                >
                  {attachment?.originalFileName}
                </Link>
              </Typography>
            </Tooltip>
          </Grid>
        )}
        {hasDivider && (
          <Grid item xs={12} className={classes.divider}>
            <Divider />
          </Grid>
        )}
      </Grid>
      <SimpleModal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(undefined)}
        title={`${t('type.colon')} ${getAttachmentLabel(attachment)}`}
        subtitle={toEllipsis(attachment.originalFileName || '', 40)}
      >
        <embed
          type={attachment.mimeType}
          src={previewFile}
          alt={getAttachmentLabel(attachment)}
          width="100%"
          height="100%"
        />
      </SimpleModal>
    </>
  );
}
